import { mapApiKey, speedColor, statusConst } from "../../const";
import React, { useCallback, useEffect, useRef, useState } from "react";
import {
  APIProvider,
  ControlPosition,
  Map,
  MapControl,
} from "@vis.gl/react-google-maps";
import { formatFromJsonDate } from "../../dateConfig";
import LiveVehicle from "./google/LiveVehicle";
import LoadingSpinner from "../LoadingSpinner";
import LocationMarker from "./google/LocationMarker";
import Polyline from "./google/Polyline";
import Toggle from "../input/Toggle";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faLocationCrosshairs } from "@fortawesome/free-solid-svg-icons";
import { deepEqual, hasDuplicateSubscriptions } from "../../helper";
import ZoomControl from "./google/ZoomControl";

const API_KEY = mapApiKey;

// Path color
const getColorBySpeed = (speed) => {
  if (speed > 40) return speedColor.HIGH;
  // if (speed > 40) return speedColor.MEDIUM;
  return speedColor.LOW;
};

const INITIAL_CAMERA = {
  center: { lat: 6.9271, lng: 79.861244 },
  zoom: 12,
};

const LiveMap = ({
  requestStartDate,
  terminalId,
  wsConnection,
  vehicleDetails,
  locations,
  activeTab = "",
  timeUpdateCompleted,
  lastDepartureTime,
  firstArrivalTime,
}) => {
  const [cameraProps, setCameraProps] = useState(INITIAL_CAMERA);
  const [vehicleLiveLocation, setVehicleLiveLocation] = useState({
    speed: 0,
    lat: 6.9271,
    lng: 79.861244,
    direction: 0,
    vehicleNo: "",
    lastUpdateTime: null,
  });
  const [centerOnVehicle, setCenterOnVehicle] = useState(true);
  const [locationInitialized, setLocationInitialized] = useState(false);
  const [vehiclePathData, setVehiclePathData] = useState([]);
  const [vehicleHistoryData, setVehicleHistoryData] = useState([]);
  const [showPathHistory, setShowPathHistory] = useState(timeUpdateCompleted);
  const [lastUpdateTime, setLastUpdateTime] = useState(null);
  const [wsStatus, setWsStatus] = useState(statusConst.IDLE);
  const [connected, setConnected] = useState(false);
  const [loadingMessage, setLoadingMessage] = useState(
    "Establishing Connection",
  );
  const [currentTerminalId, setCurrentTerminalId] = useState(terminalId);
  const [timeInitialized, setTimeInitialized] = useState(false);

  const centerOnVehicleRef = useRef(centerOnVehicle);
  const [sentPayload, setSentPayload] = useState(null);

  useEffect(() => {
    centerOnVehicleRef.current = centerOnVehicle;
  }, [centerOnVehicle]);

  useEffect(() => {
    setCurrentTerminalId(terminalId);
  }, [terminalId]);

  useEffect(() => {
    if (timeUpdateCompleted && locations && locations.length >= 2) {
      const startLocation = locations.find((location) => location.type === 1);
      const endLocation = locations.find((location) => location.type === 2);
      if (startLocation && endLocation) {
        const mappedStartLocation = {
          lat: parseFloat(startLocation.lat),
          lng: parseFloat(startLocation.lon),
        };

        const mappedEndLocation = {
          lat: parseFloat(endLocation.lat),
          lng: parseFloat(endLocation.lon),
        };

        const center = {
          lat: (mappedStartLocation.lat + mappedEndLocation.lat) / 2,
          lng: (mappedStartLocation.lng + mappedEndLocation.lng) / 2,
        };

        const bounds = new window.google.maps.LatLngBounds();
        bounds.extend(mappedStartLocation);
        bounds.extend(mappedEndLocation);
        setCameraProps((prevState) => ({
          ...prevState,
          center: center,
          zoom: 8,
        }));
      }
    }
  }, [locations, timeUpdateCompleted]);

  const liveLocationTopic = `/topic/liveLocation/${currentTerminalId}`;
  const liveLocationMessageMapping = `/locationservice/live-location/${currentTerminalId}`;
  const historyTopic = `/user/queue/locationHistory/${currentTerminalId}`;
  const historyMessageMapping = `/locationservice/location-history/vehicle`;

  const onMessage = useCallback(
    (data) => {
      data.forEach((info) => {
        const newPosition = {
          lat: info.latitude,
          lng: info.longitude,
        };

        setVehicleLiveLocation({
          ...newPosition,
          speed: info.speed,
          direction: info.direction,
          vehicleNo: info.vehicleNo,
          lastUpdateTime: info.dateTime
            ? formatFromJsonDate(info.dateTime)
            : "N/A",
        });
        setLastUpdateTime(info.dateTime);
        if (centerOnVehicleRef.current) {
          setCameraProps((prevState) => {
            console.log("Updating camera center to:", newPosition);
            return {
              ...prevState,
              center: newPosition,
            };
          });
          setLocationInitialized(true);
        }

        setVehiclePathData((prevState) => [
          ...prevState,
          { ...newPosition, speed: info.speed },
        ]);
        if (wsStatus === statusConst.PENDING) {
          setWsStatus(statusConst.SUCCESS);
          setTimeInitialized(true);
        }
      });
    },
    [wsStatus],
  );

  const onHistoryMessage = useCallback((data) => {
    data.forEach((info) => {
      setVehicleHistoryData((prevState) => [
        ...prevState,
        {
          lat: info?.latitude,
          lng: info?.longitude,
          speed: info?.speed,
        },
      ]);
    });
  }, []);

  // Connecting to WebSocket.
  useEffect(() => {
    (async () => {
      try {
        setWsStatus(statusConst.PENDING);
        await wsConnection.connect();
        setConnected(true);
        setLoadingMessage(
          timeUpdateCompleted ? "Fetching path data" : "Fetching Live location",
        );
      } catch (e) {
        setWsStatus(statusConst.FAILURE);
        setLoadingMessage("Error occurred while establishing connection");
      }
    })();

    return () => {
      wsConnection.disconnect();
    };
  }, [wsConnection]);

  // Subscribing to liveLocation
  useEffect(() => {
    if (connected && currentTerminalId && !timeUpdateCompleted) {
      if (!hasDuplicateSubscriptions(wsConnection, liveLocationTopic)) {
        wsConnection.subscribe(liveLocationTopic, (data) => {
          onMessage(data);
          if (wsStatus === statusConst.PENDING) {
            setWsStatus(statusConst.SUCCESS);
          }
        });
      }
      wsConnection.send(
        liveLocationMessageMapping,
        { "content-type": "application/json" },
        JSON.stringify({}),
      );
    }
  }, [
    wsConnection,
    connected,
    wsStatus,
    onMessage,
    liveLocationTopic,
    liveLocationMessageMapping,
    timeUpdateCompleted,
    currentTerminalId,
  ]);

  // Subscribing to history
  useEffect(() => {
    if (
      currentTerminalId &&
      connected &&
      (timeInitialized || timeUpdateCompleted) &&
      showPathHistory
    ) {
      if (!hasDuplicateSubscriptions(wsConnection, historyTopic)) {
        wsConnection.subscribe(historyTopic, (data) => {
          onHistoryMessage(data);
          if (wsStatus === statusConst.PENDING) {
            setWsStatus(statusConst.SUCCESS);
          }
        });
      }
      const payLoad = {
        terminalId: currentTerminalId,
        dateStart: timeUpdateCompleted ? firstArrivalTime : requestStartDate,
        dateEnd: timeUpdateCompleted ? lastDepartureTime : lastUpdateTime,
      };
      if (!deepEqual(payLoad, sentPayload)) {
        setSentPayload(payLoad);
        wsConnection.send(
          historyMessageMapping,
          { "content-type": "application/json" },
          JSON.stringify({
            terminalId: currentTerminalId,
            dateStart: timeUpdateCompleted
              ? firstArrivalTime
              : requestStartDate,
            dateEnd: timeUpdateCompleted ? lastDepartureTime : lastUpdateTime,
          }),
        );
      }
    }
  }, [
    wsConnection,
    connected,
    showPathHistory,
    historyTopic,
    historyMessageMapping,
    onHistoryMessage,
    timeInitialized,
    wsStatus,
    currentTerminalId,
    requestStartDate,
    timeUpdateCompleted,
    lastDepartureTime,
    firstArrivalTime,
  ]);

  const pathSegments = vehiclePathData.slice(2).map((point, index) => ({
    path: [
      {
        lat: vehiclePathData[index].lat,
        lng: vehiclePathData[index].lng,
      },
      { lat: point.lat, lng: point.lng },
    ],
    color: getColorBySpeed(point.speed),
  }));

  const historySegments = vehicleHistoryData.slice(1).map((point, index) => ({
    path: [
      {
        lat: vehicleHistoryData[index].lat,
        lng: vehicleHistoryData[index].lng,
      },
      { lat: point.lat, lng: point.lng },
    ],
    color: getColorBySpeed(point.speed),
  }));

  const handleCameraChange = useCallback((ev) => {
    setCameraProps(ev.detail);
  }, []);

  // Unsubscribe from path history.
  useEffect(() => {
    if (!showPathHistory) {
      wsConnection.unsubscribe(
        `/user/queue/locationHistory/${currentTerminalId}`,
      );
      setVehicleHistoryData([]);
    }
  }, [showPathHistory, wsConnection, currentTerminalId]);

  return (
    <>
      {wsStatus !== statusConst.SUCCESS ? (
        <LoadingSpinner
          spinner={wsStatus === statusConst.FAILURE ? "error" : "gps-loader"}
          message={loadingMessage}
        />
      ) : (
        <APIProvider apiKey={API_KEY}>
          <Map
            {...cameraProps}
            reuseMaps={true}
            style={
              activeTab === "vehicle" || activeTab === "driver"
                ? { width: 0, height: 0 }
                : { width: "100%", height: "100%" }
            }
            gestureHandling={"greedy"}
            disableDefaultUI={true}
            mapId={"live-location-gm"}
            onCameraChanged={handleCameraChange}
            onDragend={() => {
              if (locationInitialized && centerOnVehicle) {
                setCenterOnVehicle(false);
              }
            }}
          >
            <MapControl position={ControlPosition.RIGHT_BOTTOM}>
              <div
                style={{
                  display: "flex",
                  flexDirection: "column",
                  width: "120px",
                  gap: "15px",
                }}
              >
                <ZoomControl
                  onZoomIn={() => {
                    setCameraProps((prevState) => ({
                      ...prevState,
                      zoom: prevState.zoom + 1,
                    }));
                  }}
                  onZoomOut={() => {
                    setCameraProps((prevState) => ({
                      ...prevState,
                      zoom: prevState.zoom - 1,
                    }));
                  }}
                  zoom={cameraProps.zoom}
                />
              </div>
            </MapControl>
            {!timeUpdateCompleted && (
              <MapControl position={ControlPosition.LEFT_BOTTOM}>
                <div
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    backgroundColor: "rgba(255, 255, 255, 1)",
                    width: "200px",
                    position: "absolute",
                    bottom: "0",
                    left: "0",
                    padding: "10px",
                    borderRadius: "0 5px 0 0",
                    gap: "15px",
                    boxShadow: "rgba(0, 0, 0, 0.3) 0px 1px 4px -1px",
                  }}
                >
                  <div style={{ display: "flex", gap: "15px" }}>
                    <b>Show Path History</b>
                    <span>
                      <Toggle
                        value={showPathHistory}
                        onChange={() => {
                          setShowPathHistory((prevState) => !prevState);
                        }}
                      />
                    </span>
                  </div>
                </div>
              </MapControl>
            )}
            {!timeUpdateCompleted && (
              <MapControl position={ControlPosition.RIGHT_BOTTOM}>
                <div
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    backgroundColor: "rgba(255, 255, 255, 1)",
                    position: "absolute",
                    zIndex: "101",
                    bottom: "25px",
                    right: "70px",
                    padding: "10px",
                    borderRadius: "5px",
                    gap: "15px",
                    boxShadow: "rgba(0, 0, 0, 0.3) 0px 1px 4px -1px",
                  }}
                >
                  <FontAwesomeIcon
                    style={{
                      height: "30px",
                      width: "30px",
                      color: centerOnVehicle ? "#3498db" : "grey",
                      cursor: "pointer",
                    }}
                    icon={faLocationCrosshairs}
                    onClick={() => setCenterOnVehicle(true)}
                  />
                </div>
              </MapControl>
            )}
            {!timeUpdateCompleted && (
              <LiveVehicle
                vehicle={{
                  ...vehicleLiveLocation,
                  currentTerminalId,
                  model: vehicleDetails && vehicleDetails.vehModelName,
                  manufacturer:
                    vehicleDetails && vehicleDetails.vehManufacturerName,
                }}
              />
            )}
            {locations.map((location, index) => (
              <LocationMarker
                key={index}
                position={{
                  lng: parseFloat(location.lon),
                  lat: parseFloat(location.lat),
                }}
                type={location.type}
                name={location.locationName}
                typeName={
                  location.type === 1
                    ? "Start Location"
                    : location.type === 2
                      ? "End Location"
                      : "Via Location"
                }
                simpleMode={true}
                locName={location.locationName}
              />
            ))}
            {pathSegments.map((segment, index) => (
              <Polyline
                key={index}
                strokeWeight={5}
                strokeColor={segment.color}
                path={segment.path}
              />
            ))}

            {showPathHistory &&
              historySegments.map((segment, index) => (
                <Polyline
                  key={index}
                  strokeWeight={5}
                  strokeColor={segment.color}
                  path={segment.path}
                />
              ))}
          </Map>
        </APIProvider>
      )}
    </>
  );
};

export default LiveMap;
